import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    LogOut, Download, IndianRupee, ToggleLeft, MessageSquareQuote,
    Users, Plus, Trash2, Save, Lock, Sparkles, Tag, Star, Edit3, X,
    LifeBuoy, Copy, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function Admin() {
    const location = useLocation();
    const [authState, setAuthState] = useState(location.state?.user ? "in" : null); // null=checking, in, out
    const [user, setUser] = useState(location.state?.user || null);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await ax.get("/auth/me");
            setUser(data);
            setAuthState("in");
        } catch {
            setAuthState("out");
        }
    }, []);

    useEffect(() => {
        if (window.location.hash?.includes("session_id=")) return; // callback handles it
        if (location.state?.error) toast.error("That account isn't authorised for admin access.");
        if (location.state?.user) { setAuthState("in"); return; }
        checkAuth();
    }, [checkAuth, location.state]);

    const login = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try { await ax.post("/auth/logout"); } catch { /* ignore */ }
        setAuthState("out");
        setUser(null);
    };

    if (authState === null) {
        return (
            <div className="min-h-screen bg-aq-navy flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-aq-gold border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (authState === "out") {
        return (
            <div className="min-h-screen bg-aq-navy flex items-center justify-center px-5" data-testid="admin-login">
                <div className="max-w-md w-full bg-white rounded-3xl p-8 md:p-10 shadow-2xl text-center">
                    <div className="w-14 h-14 rounded-full bg-aq-navy flex items-center justify-center mx-auto mb-5">
                        <Lock className="w-6 h-6 text-aq-gold" />
                    </div>
                    <h1 className="font-serif text-3xl text-aq-navy mb-2">AcademiQ Admin</h1>
                    <p className="text-aq-muted mb-8">Sign in with your authorised Google account.</p>
                    <Button
                        onClick={login}
                        data-testid="admin-google-login"
                        className="w-full h-13 py-6 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base"
                    >
                        Sign in with Google
                    </Button>
                    <p className="text-xs text-aq-muted mt-6">Access is restricted to TheCounselCo team.</p>
                </div>
            </div>
        );
    }

    return <Dashboard user={user} onLogout={logout} />;
}

function Dashboard({ user, onLogout }) {
    const [settings, setSettings] = useState(null);
    const [leads, setLeads] = useState({ total: 0, paid: 0, leads: [] });
    const [testimonials, setTestimonials] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [rescue, setRescue] = useState({ count: 0, leads: [] });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, l, t, c, r] = await Promise.all([
                ax.get("/admin/settings"),
                ax.get("/admin/leads"),
                ax.get("/testimonials"),
                ax.get("/admin/coupons"),
                ax.get("/admin/rescue-list"),
            ]);
            setSettings(s.data);
            setLeads(l.data);
            setTestimonials(t.data);
            setCoupons(c.data);
            setRescue(r.data);
        } catch {
            toast.error("Could not load admin data.");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveSettings = async (patch) => {
        setSaving(true);
        try {
            const { data } = await ax.put("/admin/settings", patch);
            setSettings(data);
            toast.success("Saved.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    if (!settings) {
        return (
            <div className="min-h-screen bg-aq-cream flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-aq-navy border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-aq-cream" data-testid="admin-dashboard">
            {/* Header */}
            <header className="bg-aq-navy text-aq-cream sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/tcc_logo_mark.png" alt="" className="w-9 h-9 object-contain bg-white/95 rounded-lg p-1" />
                        <div className="leading-tight">
                            <div className="font-serif text-xl">AcademiQ Admin</div>
                            <div className="text-[11px] text-aq-cream/60">{user?.email}</div>
                        </div>
                    </div>
                    <Button onClick={onLogout} data-testid="admin-logout" variant="ghost" className="text-aq-cream hover:text-aq-navy hover:bg-aq-gold">
                        <LogOut className="w-4 h-4 mr-2" /> Logout
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 space-y-8">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <StatCard icon={<Users className="w-5 h-5" />} label="Total leads" value={leads.total} testId="stat-total" />
                    <StatCard icon={<IndianRupee className="w-5 h-5" />} label="Paid" value={leads.paid} testId="stat-paid" />
                    <StatCard icon={<Sparkles className="w-5 h-5" />} label="Current price" value={`₹${settings.price_paise / 100}`} testId="stat-price" />
                </div>

                {/* Price + delivery + open/closed */}
                <Panel title="Offer & availability" icon={<IndianRupee className="w-5 h-5" />}>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-aq-navy font-semibold">Price (₹)</Label>
                            <div className="flex gap-2 mt-1.5">
                                <Input
                                    type="number" defaultValue={settings.price_paise / 100}
                                    id="price-input" data-testid="price-input"
                                    className="h-11 bg-white border-aq-border"
                                />
                                <Button
                                    data-testid="price-save"
                                    onClick={() => saveSettings({ price_rupees: parseInt(document.getElementById("price-input").value, 10) })}
                                    disabled={saving} className="h-11 bg-aq-navy text-aq-cream hover:bg-aq-navy-deep"
                                >
                                    <Save className="w-4 h-4" />
                                </Button>
                            </div>
                            <p className="text-xs text-aq-muted mt-1">Changing this updates the price everywhere instantly (server-enforced).</p>
                        </div>
                        <div>
                            <Label className="text-aq-navy font-semibold">Delivery time text</Label>
                            <div className="flex gap-2 mt-1.5">
                                <Input
                                    defaultValue={settings.delivery_text} id="delivery-input" data-testid="delivery-input"
                                    className="h-11 bg-white border-aq-border"
                                />
                                <Button
                                    onClick={() => saveSettings({ delivery_text: document.getElementById("delivery-input").value })}
                                    disabled={saving} className="h-11 bg-aq-navy text-aq-cream hover:bg-aq-navy-deep"
                                >
                                    <Save className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-5 pt-5 border-t border-aq-border">
                        <div>
                            <div className="font-semibold text-aq-navy">Open for orders</div>
                            <div className="text-sm text-aq-muted">Turn off to pause new payments (e.g. at capacity).</div>
                        </div>
                        <Switch
                            checked={settings.open_for_orders} data-testid="open-toggle"
                            onCheckedChange={(v) => saveSettings({ open_for_orders: v })}
                        />
                    </div>
                </Panel>

                {/* Field toggles */}
                <Panel title="Assessment field requirements" icon={<ToggleLeft className="w-5 h-5" />}>
                    <p className="text-sm text-aq-muted mb-4">
                        Toggle whether these fields are mandatory in your assessment form. Your Google Apps Script
                        reads this live from <code className="text-aq-navy">/api/assessment-config</code>.
                    </p>
                    <FieldToggle
                        label="Achievements — required" testId="toggle-achievements"
                        checked={settings.field_config.achievements_required}
                        onChange={(v) => saveSettings({ achievements_required: v })}
                    />
                    <FieldToggle
                        label="Student Query — required" testId="toggle-query"
                        checked={settings.field_config.query_required}
                        onChange={(v) => saveSettings({ query_required: v })}
                    />
                </Panel>

                {/* Rescue list — paid but not started */}
                <RescueList rescue={rescue} reload={load} />

                {/* Testimonials */}
                <TestimonialsManager testimonials={testimonials} reload={load} />

                {/* Coupons */}
                <CouponsManager coupons={coupons} reload={load} />

                {/* Banner controls */}
                <BannerControls settings={settings} onSave={saveSettings} />

                {/* Leads */}
                <Panel title="Leads" icon={<Users className="w-5 h-5" />}
                    action={
                        <a href={`${API}/admin/leads.csv`} data-testid="leads-csv"
                           className="inline-flex items-center gap-2 text-sm font-semibold text-aq-navy hover:text-aq-gold">
                            <Download className="w-4 h-4" /> Export CSV
                        </a>
                    }>
                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full text-sm min-w-[640px]" data-testid="leads-table">
                            <thead>
                                <tr className="text-left text-aq-muted border-b border-aq-border">
                                    <th className="py-2 px-2">Date</th><th className="px-2">Name</th>
                                    <th className="px-2">Email</th><th className="px-2">Phone</th><th className="px-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.leads.slice(0, 100).map((l) => (
                                    <tr key={l.id} className="border-b border-aq-border/50">
                                        <td className="py-2 px-2 whitespace-nowrap text-aq-muted">{(l.created_at || "").slice(0, 10)}</td>
                                        <td className="px-2 font-medium text-aq-navy">{l.name}</td>
                                        <td className="px-2">{l.email}</td>
                                        <td className="px-2">{l.phone}</td>
                                        <td className="px-2">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                l.status === "paid" ? "bg-green-100 text-green-800" : "bg-aq-cream-warm text-aq-muted"
                                            }`}>{l.status}</span>
                                        </td>
                                    </tr>
                                ))}
                                {leads.leads.length === 0 && (
                                    <tr><td colSpan={5} className="py-8 text-center text-aq-muted">No leads yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </main>
        </div>
    );
}

function StatCard({ icon, label, value, testId }) {
    return (
        <div className="bg-white rounded-2xl border border-aq-border p-5 shadow-sm" data-testid={testId}>
            <div className="w-10 h-10 rounded-full bg-aq-gold/15 text-aq-gold flex items-center justify-center mb-3">{icon}</div>
            <div className="text-2xl font-serif text-aq-navy">{value}</div>
            <div className="text-sm text-aq-muted">{label}</div>
        </div>
    );
}

function Panel({ title, icon, action, children }) {
    return (
        <section className="bg-white rounded-3xl border border-aq-border p-6 md:p-8 shadow-sm">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-aq-navy text-aq-gold flex items-center justify-center">{icon}</div>
                    <h2 className="font-serif text-xl text-aq-navy">{title}</h2>
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function FieldToggle({ label, checked, onChange, testId }) {
    return (
        <div className="flex items-center justify-between py-3 border-b border-aq-border/50 last:border-0">
            <span className="text-aq-charcoal">{label}</span>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
        </div>
    );
}

function TestimonialsManager({ testimonials, reload }) {
    const [form, setForm] = useState({ name: "", grade: "", school: "", city: "", quote: "", excerpt: "", order: 99 });
    const [busy, setBusy] = useState(false);

    const add = async () => {
        if (!form.name.trim() || !form.quote.trim()) { toast.error("Name and quote are required."); return; }
        setBusy(true);
        try {
            await ax.post("/admin/testimonials", form);
            toast.success("Testimonial added.");
            setForm({ name: "", grade: "", school: "", city: "", quote: "", excerpt: "", order: 99 });
            reload();
        } catch { toast.error("Could not add."); } finally { setBusy(false); }
    };
    const remove = async (id) => {
        try { await ax.delete(`/admin/testimonials/${id}`); toast.success("Removed."); reload(); }
        catch { toast.error("Could not remove."); }
    };

    return (
        <Panel title="Testimonials" icon={<MessageSquareQuote className="w-5 h-5" />}>
            <div className="space-y-3 mb-6">
                {testimonials.map((t) => (
                    <div key={t.id} className="flex items-start justify-between gap-4 bg-aq-cream-warm/50 rounded-xl p-4" data-testid={`admin-testimonial-${t.id}`}>
                        <div>
                            <div className="font-semibold text-aq-navy">{t.name} <span className="text-xs text-aq-muted font-normal">· {t.grade} {t.city && `· ${t.city}`}</span></div>
                            <div className="text-sm text-aq-muted line-clamp-2 mt-1">{t.excerpt || t.quote}</div>
                        </div>
                        <button onClick={() => remove(t.id)} data-testid={`delete-testimonial-${t.id}`} className="text-red-500 hover:text-red-700 shrink-0">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="border-t border-aq-border pt-5 grid md:grid-cols-2 gap-3">
                <Input placeholder="Parent name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-testimonial-name" className="bg-white border-aq-border" />
                <Input placeholder="Grade (e.g. Mother of Grade 9)" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="bg-white border-aq-border" />
                <Input placeholder="School (optional)" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="bg-white border-aq-border" />
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white border-aq-border" />
                <Textarea placeholder="Full quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="new-testimonial-quote" className="md:col-span-2 bg-white border-aq-border" rows={3} />
                <Textarea placeholder="Short excerpt (optional — shown before 'Read full review')" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="md:col-span-2 bg-white border-aq-border" rows={2} />
                <Button onClick={add} disabled={busy} data-testid="add-testimonial" className="md:col-span-2 h-12 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold">
                    <Plus className="w-4 h-4 mr-2" /> Add testimonial
                </Button>
            </div>
        </Panel>
    );
}


// ---------------------------------------------------------------------------
// COUPONS MANAGER
// ---------------------------------------------------------------------------
const EMPTY_COUPON = {
    code: "", type: "percent", value: 10, active: true, is_featured: false,
    starts_at: "", ends_at: "", max_uses: "", note: "",
};

const STATUS_STYLES = {
    active: "bg-green-100 text-green-800",
    scheduled: "bg-blue-100 text-blue-800",
    expired: "bg-aq-cream-warm text-aq-muted",
    limit_reached: "bg-yellow-100 text-yellow-800",
    disabled: "bg-red-100 text-red-800",
};
const STATUS_LABELS = {
    active: "Active", scheduled: "Scheduled", expired: "Expired",
    limit_reached: "Limit reached", disabled: "Disabled",
};

// Convert an ISO UTC string to the value a <input type="datetime-local"> expects
// (local wall-clock time). Returns "" for null/blank input.
function isoToLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
    if (!local) return null;
    const d = new Date(local); // interpreted as local time
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

function CouponsManager({ coupons, reload }) {
    const [editing, setEditing] = useState(null); // null | 'new' | code string
    const [form, setForm] = useState(EMPTY_COUPON);
    const [busy, setBusy] = useState(false);

    const startNew = () => {
        setForm(EMPTY_COUPON);
        setEditing("new");
    };

    const startEdit = (c) => {
        setForm({
            code: c.code,
            type: c.type,
            value: c.value,
            active: c.active,
            is_featured: c.is_featured,
            starts_at: isoToLocalInput(c.starts_at),
            ends_at: isoToLocalInput(c.ends_at),
            max_uses: c.max_uses ?? "",
            note: c.note || "",
        });
        setEditing(c.code);
    };

    const cancel = () => { setEditing(null); setForm(EMPTY_COUPON); };

    const save = async () => {
        const payload = {
            code: form.code.trim().toUpperCase(),
            type: form.type,
            value: parseInt(form.value, 10),
            active: !!form.active,
            is_featured: !!form.is_featured,
            starts_at: localInputToIso(form.starts_at),
            ends_at: localInputToIso(form.ends_at),
            max_uses: form.max_uses === "" || form.max_uses === null ? null : parseInt(form.max_uses, 10),
            note: form.note,
        };
        setBusy(true);
        try {
            if (editing === "new") {
                await ax.post("/admin/coupons", payload);
                toast.success("Coupon created.");
            } else {
                await ax.put(`/admin/coupons/${editing}`, payload);
                toast.success("Coupon updated.");
            }
            cancel();
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save.");
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (c) => {
        try {
            await ax.put(`/admin/coupons/${c.code}`, {
                code: c.code, type: c.type, value: c.value,
                active: !c.active, is_featured: c.is_featured,
                starts_at: c.starts_at, ends_at: c.ends_at,
                max_uses: c.max_uses, note: c.note || "",
            });
            toast.success(!c.active ? `${c.code} enabled.` : `${c.code} disabled.`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update.");
        }
    };

    const del = async (c) => {
        if (!confirm(`Delete ${c.code}? This cannot be undone.`)) return;
        try {
            await ax.delete(`/admin/coupons/${c.code}`);
            toast.success("Deleted.");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete.");
        }
    };

    return (
        <Panel
            title="Coupons"
            icon={<Tag className="w-5 h-5" />}
            action={
                <Button
                    onClick={startNew}
                    data-testid="new-coupon-btn"
                    className="h-10 rounded-full bg-aq-navy hover:bg-aq-navy-deep text-aq-cream font-semibold"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> New coupon
                </Button>
            }
        >
            {coupons.length === 0 && !editing && (
                <div className="text-center text-aq-muted py-6 text-sm">No coupons yet.</div>
            )}

            <div className="overflow-x-auto -mx-2 mb-2">
                <table className="w-full text-sm min-w-[800px]" data-testid="coupons-table">
                    <thead>
                        <tr className="text-left text-aq-muted border-b border-aq-border">
                            <th className="py-2 px-2">Code</th>
                            <th className="px-2">Discount</th>
                            <th className="px-2">Status</th>
                            <th className="px-2">Uses</th>
                            <th className="px-2">Given away</th>
                            <th className="px-2">Featured</th>
                            <th className="px-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {coupons.map((c) => (
                            <tr
                                key={c.code}
                                className="border-b border-aq-border/50"
                                data-testid={`coupon-row-${c.code}`}
                            >
                                <td className="py-2.5 px-2">
                                    <span className="font-mono font-bold text-aq-navy">{c.code}</span>
                                    {c.note && (
                                        <div className="text-xs text-aq-muted italic mt-0.5">
                                            {c.note}
                                        </div>
                                    )}
                                </td>
                                <td className="px-2">{c.label}</td>
                                <td className="px-2">
                                    <span
                                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[c.status] || ""}`}
                                        data-testid={`coupon-status-${c.code}`}
                                    >
                                        {STATUS_LABELS[c.status] || c.status}
                                    </span>
                                </td>
                                <td className="px-2">
                                    <span data-testid={`coupon-uses-${c.code}`}>{c.uses}</span>
                                    {c.uses_remaining !== null && (
                                        <span className="text-xs text-aq-muted ml-1">
                                            / {c.uses + c.uses_remaining}
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 tabular-nums">₹{c.total_discount_rupees.toLocaleString("en-IN")}</td>
                                <td className="px-2">
                                    {c.is_featured && (
                                        <span
                                            className="inline-flex items-center gap-1 text-aq-gold text-xs font-semibold"
                                            data-testid={`coupon-featured-${c.code}`}
                                        >
                                            <Star className="w-3 h-3 fill-aq-gold" /> Featured
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => toggleActive(c)}
                                        data-testid={`coupon-toggle-${c.code}`}
                                        className="text-xs px-2 py-1 rounded border border-aq-border hover:bg-aq-cream-warm mr-1"
                                    >
                                        {c.active ? "Disable" : "Enable"}
                                    </button>
                                    <button
                                        onClick={() => startEdit(c)}
                                        data-testid={`coupon-edit-${c.code}`}
                                        className="text-xs px-2 py-1 rounded border border-aq-border hover:bg-aq-cream-warm mr-1"
                                    >
                                        <Edit3 className="w-3.5 h-3.5 inline" />
                                    </button>
                                    <button
                                        onClick={() => del(c)}
                                        data-testid={`coupon-delete-${c.code}`}
                                        disabled={c.uses > 0}
                                        title={c.uses > 0 ? "Used coupons cannot be deleted — disable instead" : "Delete"}
                                        className="text-xs px-2 py-1 rounded border border-aq-border hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <CouponEditor
                    form={form}
                    setForm={setForm}
                    onSave={save}
                    onCancel={cancel}
                    busy={busy}
                    isNew={editing === "new"}
                />
            )}
        </Panel>
    );
}

function CouponEditor({ form, setForm, onSave, onCancel, busy, isNew }) {
    const set = (k) => (e) => {
        const v = e?.target ? e.target.value : e;
        setForm((prev) => ({ ...prev, [k]: v }));
    };
    const toggle = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

    return (
        <div
            className="mt-4 border-t border-aq-border pt-5 grid md:grid-cols-2 gap-3"
            data-testid="coupon-editor"
        >
            <div>
                <Label className="text-aq-navy font-semibold text-xs">Code</Label>
                <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    disabled={!isNew}
                    placeholder="COUNSELCO15"
                    data-testid="coupon-code-input"
                    className="h-11 bg-white border-aq-border font-mono uppercase tracking-wider"
                />
                {!isNew && (
                    <p className="text-xs text-aq-muted mt-1">Code cannot be changed after creation.</p>
                )}
            </div>
            <div>
                <Label className="text-aq-navy font-semibold text-xs">Type</Label>
                <select
                    value={form.type}
                    onChange={set("type")}
                    data-testid="coupon-type-select"
                    className="h-11 w-full rounded-md border border-aq-border px-3 bg-white"
                >
                    <option value="percent">Percentage off</option>
                    <option value="flat">Flat rupee amount off</option>
                </select>
            </div>
            <div>
                <Label className="text-aq-navy font-semibold text-xs">
                    Value {form.type === "percent" ? "(%)" : "(paise — e.g. 30000 = ₹300)"}
                </Label>
                <Input
                    type="number"
                    value={form.value}
                    onChange={set("value")}
                    data-testid="coupon-value-input"
                    className="h-11 bg-white border-aq-border"
                />
            </div>
            <div>
                <Label className="text-aq-navy font-semibold text-xs">Max uses (blank = unlimited)</Label>
                <Input
                    type="number"
                    value={form.max_uses}
                    onChange={set("max_uses")}
                    placeholder="Unlimited"
                    data-testid="coupon-maxuses-input"
                    className="h-11 bg-white border-aq-border"
                />
            </div>
            <div>
                <Label className="text-aq-navy font-semibold text-xs">Start (local time — blank = immediate)</Label>
                <Input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={set("starts_at")}
                    data-testid="coupon-starts-input"
                    className="h-11 bg-white border-aq-border"
                />
            </div>
            <div>
                <Label className="text-aq-navy font-semibold text-xs">End (local time — blank = no expiry)</Label>
                <Input
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={set("ends_at")}
                    data-testid="coupon-ends-input"
                    className="h-11 bg-white border-aq-border"
                />
            </div>
            <div className="md:col-span-2">
                <Label className="text-aq-navy font-semibold text-xs">Internal note</Label>
                <Input
                    value={form.note}
                    onChange={set("note")}
                    placeholder="e.g. 1 Aug launch event"
                    data-testid="coupon-note-input"
                    className="h-11 bg-white border-aq-border"
                />
            </div>
            <div className="flex items-center justify-between py-2">
                <div>
                    <div className="font-semibold text-aq-navy text-sm">Active</div>
                    <div className="text-xs text-aq-muted">Kill switch — off = refused at checkout.</div>
                </div>
                <Switch
                    checked={form.active}
                    onCheckedChange={toggle("active")}
                    data-testid="coupon-active-toggle"
                />
            </div>
            <div className="flex items-center justify-between py-2">
                <div>
                    <div className="font-semibold text-aq-navy text-sm">Featured (banner)</div>
                    <div className="text-xs text-aq-muted">Only one coupon can be featured at a time.</div>
                </div>
                <Switch
                    checked={form.is_featured}
                    onCheckedChange={toggle("is_featured")}
                    data-testid="coupon-featured-toggle"
                />
            </div>
            <div className="md:col-span-2 flex gap-2 pt-2">
                <Button
                    onClick={onSave}
                    disabled={busy}
                    data-testid="coupon-save-btn"
                    className="flex-1 h-12 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold"
                >
                    <Save className="w-4 h-4 mr-2" /> {isNew ? "Create coupon" : "Save changes"}
                </Button>
                <Button
                    onClick={onCancel}
                    variant="ghost"
                    data-testid="coupon-cancel-btn"
                    className="h-12 rounded-full text-aq-navy hover:bg-aq-navy/5"
                >
                    <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// BANNER CONTROLS
// ---------------------------------------------------------------------------
function BannerControls({ settings, onSave }) {
    const [enabled, setEnabled] = useState(settings.banner_enabled ?? true);
    const [withCap, setWithCap] = useState(settings.banner_template_with_cap || "");
    const [noCap, setNoCap] = useState(settings.banner_template_no_cap || "");

    return (
        <Panel title="Launch banner" icon={<Sparkles className="w-5 h-5" />}>
            <p className="text-sm text-aq-muted mb-4">
                The banner appears on the landing page whenever a featured, active coupon exists.
                It auto-hides when the coupon becomes inactive, expires, or reaches its cap.
            </p>
            <div className="flex items-center justify-between py-3 border-b border-aq-border">
                <div>
                    <div className="font-semibold text-aq-navy">Banner enabled</div>
                    <div className="text-sm text-aq-muted">Master switch. Off = never shown, even if a featured coupon exists.</div>
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={(v) => { setEnabled(v); onSave({ banner_enabled: v }); }}
                    data-testid="banner-enabled-toggle"
                />
            </div>

            <div className="mt-5 space-y-4">
                <div>
                    <Label className="text-aq-navy font-semibold text-xs">
                        Template — when the featured coupon has a usage cap
                    </Label>
                    <p className="text-xs text-aq-muted mb-1.5">
                        Placeholders: <code>{"{uses_remaining}"}</code>, <code>{"{discount}"}</code>, <code>{"{code}"}</code>
                    </p>
                    <div className="flex gap-2">
                        <Input
                            value={withCap}
                            onChange={(e) => setWithCap(e.target.value)}
                            data-testid="banner-template-cap-input"
                            className="h-11 bg-white border-aq-border font-mono text-sm"
                        />
                        <Button
                            onClick={() => onSave({ banner_template_with_cap: withCap })}
                            data-testid="banner-template-cap-save"
                            className="h-11 bg-aq-navy text-aq-cream hover:bg-aq-navy-deep"
                        >
                            <Save className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                <div>
                    <Label className="text-aq-navy font-semibold text-xs">
                        Template — when the featured coupon has no cap
                    </Label>
                    <p className="text-xs text-aq-muted mb-1.5">
                        Placeholders: <code>{"{discount}"}</code>, <code>{"{code}"}</code>
                    </p>
                    <div className="flex gap-2">
                        <Input
                            value={noCap}
                            onChange={(e) => setNoCap(e.target.value)}
                            data-testid="banner-template-nocap-input"
                            className="h-11 bg-white border-aq-border font-mono text-sm"
                        />
                        <Button
                            onClick={() => onSave({ banner_template_no_cap: noCap })}
                            data-testid="banner-template-nocap-save"
                            className="h-11 bg-aq-navy text-aq-cream hover:bg-aq-navy-deep"
                        >
                            <Save className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </Panel>
    );
}

// ---------------------------------------------------------------------------
// RESCUE LIST — paid, but assessment not yet submitted.
// The launch-day "nobody left stranded" view.
// ---------------------------------------------------------------------------
function RescueList({ rescue, reload }) {
    const [copying, setCopying] = useState(null);

    const copyLink = async (pid) => {
        const url = `${window.location.origin}/assessment?pid=${pid}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopying(pid);
            toast.success("Assessment link copied — paste into WhatsApp/email.");
            setTimeout(() => setCopying(null), 1500);
        } catch {
            toast.error("Clipboard blocked — copy manually: " + url);
        }
    };

    const fmtTime = (h) => {
        if (h === null || h === undefined) return "—";
        if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
        if (h < 24) return `${h.toFixed(1)}h ago`;
        return `${Math.floor(h / 24)}d ago`;
    };

    const tone = (h) => {
        if (h === null) return "text-aq-muted";
        if (h < 0.5) return "text-aq-muted";
        if (h < 2) return "text-amber-700";
        return "text-red-700 font-semibold";
    };

    return (
        <Panel
            title="Payments needing follow-up"
            icon={<LifeBuoy className="w-5 h-5" />}
            action={
                <button
                    onClick={reload}
                    data-testid="rescue-refresh-btn"
                    className="flex items-center gap-1.5 text-xs text-aq-muted hover:text-aq-navy px-3 py-1.5 rounded-full border border-aq-border"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            }
        >
            <p className="text-sm text-aq-muted mb-4">
                Parents who have paid but haven't submitted the assessment yet.
                Copy their link and send it via WhatsApp — this is where you
                rescue anyone whose browser died between payment and the test.
            </p>

            {rescue.count === 0 ? (
                <div
                    className="text-center py-6 text-aq-muted text-sm rounded-2xl bg-aq-cream-warm border border-aq-border"
                    data-testid="rescue-empty"
                >
                    Every paid parent has submitted their assessment. Nothing to do here.
                </div>
            ) : (
                <div className="overflow-x-auto -mx-2">
                    <table
                        className="w-full text-sm min-w-[700px]"
                        data-testid="rescue-table"
                    >
                        <thead>
                            <tr className="text-left text-aq-muted border-b border-aq-border">
                                <th className="py-2 px-2">Parent</th>
                                <th className="px-2">Paid</th>
                                <th className="px-2">Amount</th>
                                <th className="px-2">Code</th>
                                <th className="px-2">Path</th>
                                <th className="px-2 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rescue.leads.map((l) => (
                                <tr
                                    key={l.razorpay_payment_id}
                                    className="border-b border-aq-border/50"
                                    data-testid={`rescue-row-${l.razorpay_payment_id}`}
                                >
                                    <td className="py-2.5 px-2">
                                        <div className="font-semibold text-aq-navy">{l.name}</div>
                                        <div className="text-xs text-aq-muted">
                                            {l.email} · {l.phone}
                                        </div>
                                    </td>
                                    <td className={`px-2 whitespace-nowrap ${tone(l.hours_since_paid)}`}>
                                        {fmtTime(l.hours_since_paid)}
                                    </td>
                                    <td className="px-2 tabular-nums">
                                        ₹{l.amount_rupees.toLocaleString("en-IN")}
                                    </td>
                                    <td className="px-2">
                                        {l.discount_code ? (
                                            <span className="font-mono text-xs bg-aq-gold/10 text-aq-navy border border-aq-gold/40 rounded px-1.5 py-0.5">
                                                {l.discount_code}
                                            </span>
                                        ) : (
                                            <span className="text-aq-muted text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="px-2 text-xs text-aq-muted whitespace-nowrap">
                                        {l.paid_via === "webhook" ? "Webhook only (browser never returned)" : "Browser"}
                                        {l.recovery_email_sent_at && (
                                            <div
                                                className="text-green-700 mt-0.5 flex items-center gap-1"
                                                data-testid={`rescue-emailed-${l.razorpay_payment_id}`}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"/></svg>
                                                Recovery email sent
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => copyLink(l.razorpay_payment_id)}
                                            data-testid={`rescue-copy-${l.razorpay_payment_id}`}
                                            className="text-xs px-3 py-1.5 rounded-full bg-aq-navy text-aq-cream hover:bg-aq-navy-deep inline-flex items-center gap-1.5"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                            {copying === l.razorpay_payment_id ? "Copied!" : "Copy link"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}

